import { useState } from 'react'

interface Seminar {
  id: string
  title: string
  videoUrl: string
}

const initialSeminars: Seminar[] = [
  { id: 'sem-001', title: '第33回勉強会「腸治療の現状、高濃度水素NanoGAS水の臨床応用、AIツールについて」', videoUrl: 'https://youtu.be/QCkAZLqBupY' },
  { id: 'sem-002', title: '第32回勉強会(2)「FMTを支える水素NanoGAS水・高濃度nanoGAS水」意見交換・質疑応答', videoUrl: 'https://youtu.be/VlzxB6-4YoE' },
  { id: 'sem-003', title: '第32回勉強会(1)「FMTを支える水素NanoGAS水・高濃度NanoGAS水」清水研究員', videoUrl: 'https://youtu.be/KmPt4wKJ-B0' },
  { id: 'sem-004', title: '第29回勉強会「最新の科学的知見が示す本技術の可能性」と「より公益性の高い組織としての取り組み」', videoUrl: 'https://youtu.be/vrqqdRmtPtc' },
  { id: 'sem-005', title: '第28回勉強会「口腔内細菌と腸内細菌の乱れによる全身への影響について」', videoUrl: 'https://youtu.be/orRekMRjXYU' },
]

export default function SeminarList() {
  const [seminars, setSeminars] = useState<Seminar[]>(initialSeminars)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingSeminar, setEditingSeminar] = useState<Seminar | null>(null)
  const [formData, setFormData] = useState({ title: '', videoUrl: '' })
  const [notification, setNotification] = useState<{ type: 'success' | 'error', message: string } | null>(null)

  const openCreateModal = () => {
    setEditingSeminar(null)
    setFormData({ title: '', videoUrl: '' })
    setIsModalOpen(true)
  }

  const openEditModal = (seminar: Seminar) => {
    setEditingSeminar(seminar)
    setFormData({ title: seminar.title, videoUrl: seminar.videoUrl })
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setEditingSeminar(null)
    setFormData({ title: '', videoUrl: '' })
  }

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message })
    setTimeout(() => setNotification(null), 3000)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.title || !formData.videoUrl) {
      showNotification('error', '全ての項目を入力してください')
      return
    }

    if (editingSeminar) {
      // 編集
      setSeminars(seminars.map(s => 
        s.id === editingSeminar.id 
          ? { ...s, title: formData.title, videoUrl: formData.videoUrl }
          : s
      ))
      showNotification('success', '勉強会を更新しました')
    } else {
      // 新規作成
      const newSeminar: Seminar = {
        id: `sem-${Date.now()}`,
        title: formData.title,
        videoUrl: formData.videoUrl,
      }
      setSeminars([newSeminar, ...seminars])
      showNotification('success', '勉強会を登録しました')
    }
    
    closeModal()
  }

  const handleDelete = (id: string) => {
    if (window.confirm('この勉強会を削除してもよろしいですか？')) {
      setSeminars(seminars.filter(s => s.id !== id))
      showNotification('success', '勉強会を削除しました')
    }
  }

  return (
    <>
      <header className="aip-main__header">
        <div className="aip-main__header-container">
          <h1 className="aip-main__header-title">勉強会</h1>
          <div className="aip-main__header-buttons">
            <button onClick={openCreateModal} className="aip-button aip-button--solid-fill">
              <span className="material-symbols-outlined">add</span>
              新規登録
            </button>
          </div>
        </div>
      </header>

      <div className="aip-main__content">
        {/* 通知 */}
        {notification && (
          <div className={`notification notification--${notification.type}`}>
            <span className="material-symbols-outlined">
              {notification.type === 'success' ? 'check_circle' : 'error'}
            </span>
            {notification.message}
          </div>
        )}

        <div className="list-container">
          <div className="aip-table-wrapper">
            <table className="aip-table">
              <thead>
                <tr>
                  <th>タイトル</th>
                  <th>動画URL</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {seminars.map((seminar) => (
                  <tr key={seminar.id}>
                    <td>{seminar.title}</td>
                    <td>
                      <a href={seminar.videoUrl} target="_blank" rel="noopener noreferrer" className="aip-table__link">
                        {seminar.videoUrl}
                      </a>
                    </td>
                    <td>
                      <div className="aip-table__actions">
                        <button 
                          onClick={() => openEditModal(seminar)} 
                          className="aip-button aip-button--icon aip-button--sm" 
                          title="編集"
                        >
                          <span className="material-symbols-outlined">edit</span>
                        </button>
                        <button 
                          onClick={() => handleDelete(seminar.id)} 
                          className="aip-button aip-button--icon aip-button--sm aip-button--danger" 
                          title="削除"
                        >
                          <span className="material-symbols-outlined">delete</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {seminars.length === 0 && (
            <div className="empty-state">
              <span className="material-symbols-outlined">school</span>
              <p>登録されている勉強会はありません</p>
              <button onClick={openCreateModal} className="aip-button aip-button--solid-fill">
                最初の勉強会を登録
              </button>
            </div>
          )}
        </div>
      </div>

      {/* モーダル */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal__header">
              <h2 className="modal__title">
                {editingSeminar ? '勉強会の編集' : '勉強会の新規登録'}
              </h2>
              <button onClick={closeModal} className="modal__close">
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <form onSubmit={handleSubmit} className="modal__body">
              <div className="form-group">
                <label className="form-label">
                  タイトル <span className="required">*</span>
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="例：第34回勉強会「テーマ名」"
                />
              </div>
              <div className="form-group">
                <label className="form-label">
                  動画URL <span className="required">*</span>
                </label>
                <input
                  type="url"
                  className="form-input"
                  value={formData.videoUrl}
                  onChange={(e) => setFormData({ ...formData, videoUrl: e.target.value })}
                  placeholder="https://youtu.be/..."
                />
              </div>
              <div className="modal__footer">
                <button type="button" onClick={closeModal} className="aip-button aip-button--outline">
                  キャンセル
                </button>
                <button type="submit" className="aip-button aip-button--solid-fill">
                  {editingSeminar ? '更新' : '登録'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        .list-container {
          background-color: var(--background-primary);
          border-radius: var(--border-radius-lg);
          box-shadow: var(--shadow-sm);
        }

        .aip-table-wrapper {
          overflow-x: auto;
        }

        .aip-table {
          width: 100%;
          border-collapse: collapse;
        }

        .aip-table th, .aip-table td {
          padding: var(--spacing-sm) var(--spacing-md);
          text-align: left;
          border-bottom: 1px solid var(--border-color);
        }

        .aip-table th {
          background-color: var(--color-solid-gray25);
          font-weight: 500;
          font-size: 0.75rem;
          color: var(--text-secondary);
        }

        .aip-table tbody tr:hover {
          background-color: var(--color-solid-gray25);
        }

        .aip-table__link {
          color: var(--color-blue600);
        }

        .aip-table__actions {
          display: flex;
          gap: var(--spacing-xs);
        }

        .aip-button--icon {
          padding: var(--spacing-xs);
          border: none;
          background: none;
          color: var(--text-secondary);
          cursor: pointer;
          border-radius: var(--border-radius-sm);
        }

        .aip-button--icon:hover {
          background-color: var(--color-solid-gray100);
        }

        .aip-button--danger:hover {
          color: var(--color-alert);
          background-color: var(--color-alert-light);
        }

        .aip-button--sm .material-symbols-outlined {
          font-size: 20px;
        }

        .empty-state {
          padding: var(--spacing-xl);
          text-align: center;
          color: var(--text-secondary);
        }

        .empty-state .material-symbols-outlined {
          font-size: 48px;
          margin-bottom: var(--spacing-md);
        }

        .empty-state p {
          margin-bottom: var(--spacing-md);
        }

        /* 通知 */
        .notification {
          display: flex;
          align-items: center;
          gap: var(--spacing-sm);
          padding: var(--spacing-md);
          border-radius: var(--border-radius-md);
          margin-bottom: var(--spacing-md);
          animation: slideIn 0.3s ease;
        }

        .notification--success {
          background-color: var(--color-success-light);
          color: var(--color-success);
        }

        .notification--error {
          background-color: var(--color-alert-light);
          color: var(--color-alert);
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* モーダル */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          animation: fadeIn 0.2s ease;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .modal {
          background-color: var(--background-primary);
          border-radius: var(--border-radius-lg);
          box-shadow: var(--shadow-lg);
          width: 100%;
          max-width: 600px;
          max-height: 90vh;
          overflow: hidden;
          animation: slideUp 0.3s ease;
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .modal__header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: var(--spacing-md) var(--spacing-lg);
          border-bottom: 1px solid var(--border-color);
        }

        .modal__title {
          font-size: 1.125rem;
          font-weight: 600;
        }

        .modal__close {
          background: none;
          border: none;
          color: var(--text-secondary);
          cursor: pointer;
          padding: var(--spacing-xs);
          border-radius: var(--border-radius-sm);
        }

        .modal__close:hover {
          background-color: var(--color-solid-gray100);
        }

        .modal__body {
          padding: var(--spacing-lg);
        }

        .form-group {
          margin-bottom: var(--spacing-md);
        }

        .form-label {
          display: block;
          font-size: 0.875rem;
          font-weight: 500;
          margin-bottom: var(--spacing-xs);
        }

        .required {
          color: var(--color-alert);
        }

        .form-input {
          width: 100%;
          padding: var(--spacing-sm) var(--spacing-md);
          border: 1px solid var(--border-color);
          border-radius: var(--border-radius-md);
          font-size: 0.875rem;
        }

        .form-input:focus {
          outline: none;
          border-color: var(--color-blue500);
          box-shadow: 0 0 0 3px var(--color-blue100);
        }

        .modal__footer {
          display: flex;
          justify-content: flex-end;
          gap: var(--spacing-sm);
          margin-top: var(--spacing-lg);
          padding-top: var(--spacing-md);
          border-top: 1px solid var(--border-color);
        }
      `}</style>
    </>
  )
}
